"use client";

import { useRef } from "react";
import { Search } from "lucide-react";
import { GradText, SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks, WipTag } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { roadmapItem } from "@/lib/roadmap";
import { routePath } from "@/lib/routes";
import LinkRow, { LinkSheet, RowChip } from "./LinkRow";

const LIST_SUMMARY =
  "Example of the link list filtered to go.example.com: four links in matching order — /launch, " +
  "/keynote, /promo and /deck — each with its destination, status code and position.";

const CSV_SUMMARY =
  "Example CSV import file with three columns — slug, destination, status — and three rows: " +
  "launch, keynote and promo. Bulk import via CSV is on the roadmap.";

const ROWS = [
  { slug: "/launch", dest: "example.com/launch", code: 302 },
  { slug: "/keynote", dest: "youtube.com/live/…", code: 302 },
  { slug: "/promo", dest: "example.com/promo?ref=ig", code: 302 },
  { slug: "/deck", dest: "example.com/investors.pdf", code: 301 },
];

const CSV = [
  ["slug", "destination", "status"],
  ["launch", "https://example.com/launch", "302"],
  ["keynote", "https://youtube.com/live/…", "302"],
  ["promo", "https://example.com/promo?ref=ig", "302"],
];

const csvImport = roadmapItem("csv-import");

/**
 * Organized at scale — what the dashboard's link list really does (search by
 * path, filter by domain and response type, drag into matching order) plus
 * the inventory moving in as CSV. There are no tags or folders (claims audit
 * 2026-09-27): the import artifact carries the real roadmap sash from
 * lib/roadmap.ts. Export is live (backend#479): Settings → Export, owner/admin.
 */
export default function OrganizeSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-rise]", { y: 22, stagger: 0.08 });

  return (
    <SectionShell id="organize" className="scroll-mt-16">
      <SectionHead
        eyebrow="Organized at scale"
        title={
          <>
            Five hundred links, <GradText>still one screen.</GradText>
          </>
        }
        lead="Search by path, filter by domain or response type, and drag links into the order they match in — with the same instant sync a single edit gets."
      />

      <div ref={rootRef} className="flex flex-col">
        <div data-grid-block className="grid items-start gap-[46px] lg:grid-cols-[1.1fr_1fr]">
          {/* The filtered list */}
          <div data-rise className="min-w-0">
            <p className="sr-only">{LIST_SUMMARY}</p>
            <div aria-hidden="true">
              <LinkSheet
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <Search size={12} aria-hidden /> GO.EXAMPLE.COM · 4 LINKS
                  </span>
                }
                note="IN MATCHING ORDER"
              >
                {ROWS.map((r, i) => (
                  <LinkRow
                    key={r.slug}
                    slug={r.slug}
                    destination={r.dest}
                    code={r.code}
                    extras={<RowChip>order {i + 1}</RowChip>}
                  />
                ))}
              </LinkSheet>
            </div>
            <dl className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <dt className="text-[15.5px] font-bold tracking-[-0.01em] text-ink">Search &amp; filter</dt>
                <dd className="mt-1.5 text-[14.5px] leading-[1.6] text-muted">
                  Find a link by its path, narrow the list to one domain, or show only redirects —
                  or only plain responses — then sort by order, path or creation date.
                </dd>
              </div>
              <div>
                <dt className="text-[15.5px] font-bold tracking-[-0.01em] text-ink">Matching order</dt>
                <dd className="mt-1.5 text-[14.5px] leading-[1.6] text-muted">
                  Links on one domain match top to bottom. Drag one into place and the routing
                  layer follows the new order straight away.
                </dd>
              </div>
            </dl>
          </div>

          {/* CSV in / CSV out */}
          <div data-rise className="min-w-0">
            <p className="sr-only">{CSV_SUMMARY}</p>
            <div aria-hidden="true" className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref-lg">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line bg-surface-2 px-[18px] py-[9px]">
                <span className="font-jetbrains text-[10.5px] tracking-[0.12em] text-dim">links.csv · 3 ROWS</span>
                <WipTag label={`Bulk import · ${csvImport.promised}`} />
              </div>
              {/* Wide on phones — scrolls inside the sheet, never the page. */}
              <div className="overflow-x-auto">
              <table className="w-full border-collapse font-jetbrains text-[12px]">
                <tbody>
                  {CSV.map((row, i) => (
                    <tr key={i} className={i === 0 ? "bg-surface-2/60 text-dim" : "text-ink"}>
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={`border-b border-line px-[14px] py-[9px] ${
                            j < row.length - 1 ? "border-r" : ""
                          } ${j === 1 ? "max-w-[190px] truncate" : "whitespace-nowrap"}`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
            <dl className="mt-6 grid gap-5">
              <div>
                <dt className="flex flex-wrap items-center gap-2.5 text-[15.5px] font-bold tracking-[-0.01em] text-ink">
                  Bulk import
                </dt>
                <dd className="mt-1.5 text-[14.5px] leading-[1.6] text-muted">
                  One row per link — slug, destination, status. Collisions get flagged for review,
                  never silently renamed. Ships {csvImport.promised}; the date lives on the{" "}
                  <a href={routePath("changelog")} className="font-semibold text-ink underline decoration-line-3 underline-offset-[3px] hover:text-brand-600">
                    changelog
                  </a>{" "}
                  until it does.
                </dd>
              </div>
              <div>
                <dt className="text-[15.5px] font-bold tracking-[-0.01em] text-ink">Export</dt>
                <dd className="mt-1.5 text-[14.5px] leading-[1.6] text-muted">
                  Settings → Export downloads every link in the workspace in one file: a CSV with one
                  row per link, or lossless JSON with the routing rules. Workspace owners and admins can
                  run it, and your domain stays yours —{" "}
                  <a href={routePath("customDomains")} className="font-semibold text-ink underline decoration-line-3 underline-offset-[3px] hover:text-brand-600">
                    custom domains
                  </a>{" "}
                  covers re-pointing it.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
